/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Box, Button, Text } from '@tishman/components';
import * as Icons from '@tishman/icons';

interface ExpandButtonProps {
  isExpanded: boolean;
  label?: string;
  onClick: () => void;
}

const ExpandButton: React.FC<ExpandButtonProps> = ({
  isExpanded,
  label = 'See more info',
  onClick,
}) => (
  <Box sx={{ display: 'block', marginTop: 3 }}>
    <Button
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        textDecoration: 'underline',
        textUnderlineOffset: '3px',
      }}
      variant="text"
    >
      {label}
      <Box
        as="span"
        sx={{
          transition: 'transform 0.2s ease',
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          marginTop: isExpanded ? '-3px' : '0',
          display: 'inline-flex',
        }}
      >
        <Icons.ChevronDown height={20} width={20} />
      </Box>
    </Button>
  </Box>
);

interface BaseExpandableContentProps {
  label?: string;
  text: string | React.JSX.Element;
  maxLines?: number;
}

const PreviewExpandableContent: React.FC<BaseExpandableContentProps> = ({
  label,
  text,
  maxLines = 3,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const lineHeight = parseInt(
        window.getComputedStyle(textRef.current).lineHeight,
      );
      const height = textRef.current.scrollHeight;
      setContentHeight(height);
      const numberOfLines = Math.round(height / lineHeight);
      setHasMore(numberOfLines > maxLines);
    }
  }, [text, maxLines]);

  const maxHeight = useMemo(() => {
    return isExpanded ? `${contentHeight}px` : `${maxLines * 1.5}em`;
  }, [isExpanded, maxLines, contentHeight]);

  return (
    <Box>
      <Box
        sx={{
          transition: isExpanded
            ? 'max-height 0.3s ease-in-out'
            : 'max-height 0.2s ease-out',
          maxHeight,
          overflow: 'hidden',
        }}
      >
        {typeof text === 'string' ? (
          <Text
            ref={textRef}
            sx={{
              lineHeight: '1.5',
              transition: 'all 0.2s ease-out',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              WebkitLineClamp: isExpanded ? 'unset' : maxLines,
              transitionDelay: isExpanded ? '0s' : '0.15s',
            }}
          >
            {text}
          </Text>
        ) : (
          <Box
            ref={textRef}
            sx={{
              lineHeight: '1.5',
              transition: 'all 0.2s ease-out',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              WebkitLineClamp: isExpanded ? 'unset' : maxLines,
              transitionDelay: isExpanded ? '0s' : '0.15s',
            }}
          >
            {text}
          </Box>
        )}
      </Box>
      {hasMore && (
        <ExpandButton
          isExpanded={isExpanded}
          label={label}
          onClick={() => setIsExpanded((v) => !v)}
        />
      )}
    </Box>
  );
};

const ToggleExpandableContent: React.FC<BaseExpandableContentProps> = ({
  label,
  text,
  maxLines = 3,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef.current) {
      const height = textRef.current.scrollHeight;
      setContentHeight(height);
    }
  }, [text]);

  return (
    <Box>
      <ExpandButton
        isExpanded={isExpanded}
        label={label}
        onClick={() => setIsExpanded((v) => !v)}
      />
      <Box
        sx={{
          marginTop: 3,
          transition: isExpanded
            ? 'max-height 0.3s ease-in-out'
            : 'max-height 0.2s ease-out',
          maxHeight: isExpanded ? `${contentHeight}px` : 0,
          overflow: 'hidden',
        }}
      >
        {typeof text === 'string' ? (
          <Text ref={textRef} sx={{ lineHeight: '1.5' }}>
            {text}
          </Text>
        ) : (
          <Box ref={textRef} sx={{ lineHeight: '1.5' }}>
            {text}
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface ExpandableContentProps extends BaseExpandableContentProps {
  showPreview?: boolean;
}

const ExpandableContent: React.FC<ExpandableContentProps> = ({
  showPreview = true,
  ...props
}) => {
  if (showPreview) {
    return <PreviewExpandableContent {...props} />;
  }
  return <ToggleExpandableContent {...props} />;
};

export default ExpandableContent;
